import { EAvailabilityMode, isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import {
    WEBSITE_ANALYSIS_AUDIENCE_INTERESTS,
    WEBSITE_ANALYSIS_COMPETITORS,
    WEBSITE_ANALYSIS_DEMOGRAPHICS,
    WEBSITE_ANALYSIS_FOLDERS,
    WEBSITE_ANALYSIS_GEOGRAPHY,
    WEBSITE_ANALYSIS_MARKETING_CHANNELS,
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS,
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_PUBLISHER_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_KEYWORDS,
    WEBSITE_ANALYSIS_MARKETING_REFERRAL_INCOMING,
    WEBSITE_ANALYSIS_MARKETING_REFERRAL_OUTGOING,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_COMPETITORS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_SOCIAL_OVERVIEW,
    WEBSITE_ANALYSIS_PAGES,
    WEBSITE_ANALYSIS_PERFORMANCE,
    WEBSITE_ANALYSIS_SUBDOMAINS,
    WEBSITE_ANALYSIS_TECHNOLOGIES,
    WEBSITE_ANALYSIS_TRAFFIC_ENGAGEMENT,
} from "app/pages/sales-intelligence/constants/routes";
import type { NavigationTabConfig } from "app/pages/sales-intelligence/modules/company-analysis/types/navigation-tabs";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { WEBSITE_ANALYSIS_TAB_NAMES } from "../constants";

export const WEBSITE_ANALYSIS_TABS: readonly NavigationTabConfig[] = [
    {
        stateId: WEBSITE_ANALYSIS_PERFORMANCE,
        get title() {
            return translate("si.website-analisys.tab.performance");
        },
        get isHidden() {
            return !isAvailable(swSettings.components.WsWebOverview);
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.performance,
    },
    {
        stateId: WEBSITE_ANALYSIS_TECHNOLOGIES,
        get title() {
            return translate("si.website-analisys.tab.technologies");
        },
        get isHidden() {
            return salesSettingsHelper.getWebsiteTechnologiesFeature() === EAvailabilityMode.HIDDEN;
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.technologies,
    },
    {
        stateId: WEBSITE_ANALYSIS_COMPETITORS,
        get title() {
            return translate("si.website-analisys.tab.competitors");
        },
        get isHidden() {
            return salesSettingsHelper.getWebsiteCompetitorsFeature() === EAvailabilityMode.HIDDEN;
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.competitors,
    },
    {
        stateId: WEBSITE_ANALYSIS_TRAFFIC_ENGAGEMENT,
        get title() {
            return translate("si.website-analisys.tab.traffic");
        },
        get isHidden() {
            return !isAvailable(swSettings.components.WsWebTrafficAndEngagement);
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.trafficEngagement,
    },
    {
        stateId: WEBSITE_ANALYSIS_MARKETING_CHANNELS,
        get title() {
            return translate("si.website-analisys.tab.marketing-channels");
        },
        get isHidden() {
            return !isAvailable(swSettings.components.WsWebTrafficChannels);
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingChannels,
        items: [
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW,
                get title() {
                    return translate("analysis.search.marketing-channels.overview.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WsWebSearchOverview);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingSearchOverview,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_KEYWORDS,
                get title() {
                    return translate("analysis.search.keywords.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WsWebSearchKeywords);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingKeywords,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_SEARCH_COMPETITORS,
                get title() {
                    return translate("analysis.search.organic.competitors.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WsWebOrganicSearchCompetitors);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingSearchCompetitors,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS,
                get title() {
                    return translate("analysis.search.ads.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WsWebSearchAds);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingSearchAds,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_REFERRAL_INCOMING,
                get title() {
                    return translate("analysis.referrals.incoming.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WebReferralIncomingTraffic);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingReferralIncoming,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_REFERRAL_OUTGOING,
                get title() {
                    return translate("analysis.referrals.outgoing.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WebReferralOutgoingTraffic);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingReferralOutgoing,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW,
                get title() {
                    return translate("digitalsuite.analysis.display.overview.page.title");
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingDisplayOverview,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_PUBLISHER_OVERVIEW,
                get title() {
                    return translate("si.website-analysis.tab.publisher-overview");
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingPublisherOverview,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS,
                get title() {
                    return translate("analysis.display.mediators.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WsWebDisplayTrafficAdNetworks);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingDisplayAdNetworks,
            },
            {
                stateId: WEBSITE_ANALYSIS_MARKETING_SOCIAL_OVERVIEW,
                get title() {
                    return translate("analysis.social.overview.title");
                },
                get isHidden() {
                    return !isAvailable(swSettings.components.WsWebSocialTrafficOverview);
                },
                trackingName: WEBSITE_ANALYSIS_TAB_NAMES.marketingSocialOverview,
            },
        ],
    },
    {
        stateId: WEBSITE_ANALYSIS_GEOGRAPHY,
        get isHidden() {
            return !isAvailable(swSettings.components.WebGeography);
        },
        get title() {
            return translate("si.website-analisys.tab.geography");
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.geography,
    },
    {
        stateId: WEBSITE_ANALYSIS_DEMOGRAPHICS,
        get isHidden() {
            return !isAvailable(swSettings.components.WebDemographics);
        },
        get title() {
            return translate("si.website-analysis.tab.demographics");
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.demographics,
    },
    {
        stateId: WEBSITE_ANALYSIS_AUDIENCE_INTERESTS,
        get isHidden() {
            return !isAvailable(swSettings.components.WebAudienceInterests);
        },
        get title() {
            return translate("si.website-analysis.tab.audience-interests");
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.audienceInterests,
    },
    {
        stateId: WEBSITE_ANALYSIS_SUBDOMAINS,
        get isHidden() {
            return !isAvailable(swSettings.components.WsWebContentSubdomains);
        },
        get title() {
            return translate("si.website-analysis.tab.subdomains");
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.subdomains,
    },
    {
        stateId: WEBSITE_ANALYSIS_PAGES,
        get isHidden() {
            return !isAvailable(swSettings.components.WsWebContentPages);
        },
        get title() {
            return translate("si.website-analysis.tab.pages");
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.pages,
    },
    {
        stateId: WEBSITE_ANALYSIS_FOLDERS,
        get isHidden() {
            return !isAvailable(swSettings.components.WsWebContentFolders);
        },
        get title() {
            return translate("si.website-analysis.tab.folders");
        },
        trackingName: WEBSITE_ANALYSIS_TAB_NAMES.folders,
    },
];
